import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { SplashScreen } from '@capacitor/splash-screen';
import { PushNotifications, ActionPerformed } from '@capacitor/push-notifications';
import { register } from 'swiper/element/bundle';
import { TwoNService } from './services/two-n.service';
import { IncomingCallNotificationService } from './services/incoming-call-notification.service';
import { WaterLeakNotificationService } from './services/water-leak-notification.service';
import { SevereWeatherNotificationService } from './services/severe-weather-notification.service';
import { AppInitializationService } from './services/app-initialization.service';
import { DeepLinkService } from './services/deep-link.service';
import { ThemeService } from './services/theme.service';
import { filter, Subscription } from 'rxjs';
import { TwoNNotificationEvent } from '@Vessel-Tech/2n-intercom';
import { Platform } from '@ionic/angular';
import { AnalyticsService } from './services/analytics.service';

register();
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'VesselClientApp';
  private notificationSubscription: Subscription = new Subscription();

  constructor(
    private twoNService: TwoNService,
    private incomingCallService: IncomingCallNotificationService,
    private waterLeakService: WaterLeakNotificationService,
    private severeWeatherService: SevereWeatherNotificationService,
    private appInitService: AppInitializationService,
    private deepLinkService: DeepLinkService,
    private themeService: ThemeService,
    private router: Router,
    private platform: Platform,
    private analyticsService: AnalyticsService
  ) {
    // Hide splash screen immediately for faster perceived load
    SplashScreen.hide();

    // Start authentication-based initialization immediately
    // This ensures device cache is loaded as early as possible
    this.appInitService.startAuthBasedInitialization();

    // Defer non-critical initializations
    setTimeout(() => {
      // Initialize Coralogix analytics (enabled in all environments)
      this.analyticsService.initialize();

      // Initialize screen orientation in background
      this.initializeScreenOrientation();
    }, 100);
  }
  
  ngOnInit() {
    // Authentication-based initialization already started in constructor

    // Initialize water leak notification listener
    this.waterLeakService.init();

    // Initialize severe weather notification listener
    this.severeWeatherService.init();

    // Initialize deep linking
    this.deepLinkService.initializeDeepLinks();

    // Check if app was opened with a deep link
    this.deepLinkService.checkInitialUrl();

    // Track screen views automatically via Router events
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      const screenName = this.routeToScreenName(event.urlAfterRedirects || event.url);
      this.analyticsService.trackScreenView(screenName, event.urlAfterRedirects || event.url);
      this.analyticsService.trackFirstUse(`screen:${screenName}`);
    });

    // Track push notification taps (native only)
    if (this.platform.is('capacitor')) {
      PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
        try {
          this.analyticsService.trackNotificationTap(this.buildNotificationTapPayload(action));
        } catch {
          // Never break the app
        }
      });
    }

    // Subscribe to 2N notifications to handle auto-answered calls
    this.notificationSubscription = this.twoNService.notificationEvent$.subscribe(
      (event: TwoNNotificationEvent | null) => {
        // console.log('[2N] ===== APP NAVIGATION EVENT =====');
        // console.log('[2N] Received event:', event ? JSON.stringify(event, null, 2) : 'null');
        // console.log('[2N] Current time:', new Date().toISOString());
        // console.log('[2N] Current route:', this.router.url);
        
        if (event && event.type === 'intercom') {
          // console.log('[2N] Intercom notification received, navigating to intercom page');
          // console.log('[2N] Navigation reason:', event.reason || 'none provided');
          // console.log('[2N] Attempting navigation from', this.router.url, 'to /intercom');
          
          this.router.navigate(['/intercom']).then(() => {
            // Navigation complete
          }).catch(error => {
            console.error('[2N] Error during navigation to intercom page:', error);
          });
        } else {
          // console.log('[2N] Non-intercom event or null event, no navigation needed');
        }
        // console.log('[2N] ===================================');
      }
    );
  }
  
  ngOnDestroy() {
    // Clean up subscription
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    this.waterLeakService.cleanup();
    this.severeWeatherService.cleanup();
  }

  // notificationType, not type -- would collide with trackNotificationTap's own 'notification_tap' marker.
  private buildNotificationTapPayload(action: ActionPerformed): Record<string, any> {
    const data = action.notification?.data || {};
    return {
      title: action.notification?.title,
      notificationType: data.type || data.notificationType || 'unknown',
      actionId: action.actionId
    };
  }

  private routeToScreenName(url: string): string {
    // Strip query params and fragments
    const path = url.split('?')[0].split('#')[0];

    const routeMap: Record<string, string> = {
      '/dashboard/home': 'home',
      '/dashboard/building': 'building',
      '/dashboard/my-vessel': 'my_vessel',
      '/dashboard/settings': 'settings',
      '/dashboard/settings/lighting': 'settings_lighting',
      '/dashboard/settings/climate': 'settings_climate',
      '/dashboard/settings/shower': 'settings_shower',
      '/dashboard/settings/notification-settings': 'settings_notifications',
      '/dashboard/settings/general-settings': 'settings_general',
      '/dashboard/settings/access-settings': 'settings_access',
      '/dashboard/settings/early-access': 'settings_early_access',
      '/dashboard/settings/profile': 'settings_profile',
      '/dashboard/home/houseless': 'houseless',
      '/dashboard/home/remind-pin': 'remind_pin',
      '/intercom': 'intercom',
      '/tours/check-in': 'tours_check_in',
      '/tours/building': 'tours_building',
      '/tours/apartment': 'tours_apartment',
      '/tours/active': 'tours_active',
      '/intro/welcome': 'intro_welcome',
    };

    return routeMap[path] || path.replace(/^\//, '').replace(/\//g, '_') || 'unknown';
  }

  private async initializeScreenOrientation() {
    try {
      // Only lock orientation on native platforms
      if (this.platform.is('ios') || this.platform.is('android')) {
        await ScreenOrientation.lock({ orientation: 'portrait' });
      }
    } catch (error) {
      // console.log('ScreenOrientation API not available in this environment');
    }
  }
}
